import { useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Switch, Select, Tooltip as AntdTooltip } from "antd";
import { BarChartOutlined, LineChartOutlined } from "@ant-design/icons";
import { Flex } from "src/components/Flex";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { TimeSeries } from "src/search/components/TimeSeries";
import { ExploreContext } from "../useExploreContext";
import { VisualizationProps } from "./visualization-props";
import { TimeUnit, isTimeUnit } from "./FormattingControls";

type Config = { unit: TimeUnit; displayType: "bars" | "line"; labels: boolean };

const toConfig: (data: any) => Config = (data) => ({
  unit: isTimeUnit(data?.unit) ? data.unit : "auto",
  displayType: data?.displayType === "line" ? "line" : "bars",
  labels: data?.labels === false ? false : true,
});

export function OverTime(props: VisualizationProps) {
  const config = useMemo(
    () => toConfig(props.context.view.config),
    [props.context.view.config]
  );

  // So fn obnoxious.
  const { setTimeUnit } = props.context.timeSeries;
  const setTimeUnitFn = useCallback(
    (unit: TimeUnit) => setTimeUnit(unit),
    [setTimeUnit]
  );
  const timeUnitSetter = useRef({ fn: setTimeUnitFn });
  timeUnitSetter.current = { fn: setTimeUnitFn };

  useEffect(() => {
    timeUnitSetter.current.fn(config.unit);
  }, [config.unit]);
  return <Ready {...props} {...config} />;
}

export function Ready({
  displayType,
  labels,
  context,
}: Config & VisualizationProps) {
  return (
    <DynamicCanvas>
      {({ height, width }) => (
        <Flex direction="column" style={{ height: "100%" }}>
          <div
            style={{
              padding: 0,
              width: "100%",
              overflow: height < 150 ? "hidden" : undefined,
            }}
          >
            <TimeSeries
              context={context}
              displayType={displayType}
              hideLabels={!labels}
              height={Math.min(150, height)}
              resizeTriggerKey={`${width}-${height}`}
            />
          </div>
        </Flex>
      )}
    </DynamicCanvas>
  );
}

export function OverTimeControls(props: { context: ExploreContext }) {
  const { timeSeries, view } = props.context;
  const config = useMemo(() => toConfig(view.config), [view.config]);
  const isAuto = config.unit === "auto";

  return (
    <div>
      <div className="section">
        <div className="section-header">Display</div>
        <div className="layout-control-grid">
          <AntdTooltip title="Bar Chart">
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, displayType: "bars" })}
              className={`layout-control-button ${
                config.displayType === "bars" ? "selected" : ""
              }`}
            >
              <BarChartOutlined />
              <span className="layout-control-button-label">Bars</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Line Chart">
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, displayType: "line" })}
              className={`layout-control-button ${
                config.displayType === "line" ? "selected" : ""
              }`}
            >
              <LineChartOutlined />
              <span className="layout-control-button-label">Line</span>
            </Button>
          </AntdTooltip>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Data</div>
        <Flex style={{ width: "100%", marginTop: 18 }} spaceBetween>
          <div
            className={[
              "viz-label",
              timeSeries.autoTimeUnit ? "disabled" : "",
            ].join(" ")}
          >
            Time unit
          </div>
          <Select
            className={"viz-select"}
            disabled={isAuto}
            bordered={false}
            popupMatchSelectWidth={false}
            options={[
              {
                value: "days",
                label: <span className="viz-select-option">Days</span>,
              },
              {
                value: "hours",
                label: <span className="viz-select-option">Hours</span>,
              },
              {
                value: "minutes",
                label: <span className="viz-select-option">Minutes</span>,
              },
            ]}
            value={isAuto ? timeSeries.autoTimeUnit : config.unit}
            onChange={(unit) => view.setConfig({ ...config, unit })}
          />
        </Flex>

        <label style={{ cursor: "pointer", width: "100%", marginTop: 18 }}>
          <Flex spaceBetween style={{ paddingRight: 2, marginTop: 12 }}>
            <span className="viz-label">Automatic time unit</span>
            <Switch
              checked={isAuto}
              size="small"
              onChange={(checked) => {
                if (checked) view.setConfig({ ...config, unit: "auto" });
                else
                  view.setConfig({ ...config, unit: timeSeries.autoTimeUnit });
              }}
            />
          </Flex>
        </label>

        <label style={{ cursor: "pointer", width: "100%", marginTop: 18 }}>
          <Flex spaceBetween style={{ paddingRight: 2, marginTop: 12 }}>
            <span className="viz-label">Show labels</span>
            <Switch
              checked={config.labels}
              size="small"
              onChange={(checked) =>
                view.setConfig({ ...config, labels: checked })
              }
            />
          </Flex>
        </label>
      </div>
    </div>
  );
}
